import type { CSSProperties } from "react";
import { useApp } from "../state/AppState";
import type { PayPlan } from "../state/AppState";

/**
 * Le bouton qui mène au paiement — et qui sait s'il a déjà été suivi.
 *
 * Le produit n'avait aucune mémoire de sa seule transaction. Une fois la
 * fenêtre fermée, l'écran de résultat, le bloc d'offres et le plan d'actions
 * continuaient d'afficher « Obtenir mon rapport — 3 000 F » à quelqu'un qui
 * venait de payer, sans lui laisser le moindre chemin vers ce qu'il avait
 * acheté. Deux torts en un : on redemandait de l'argent, et on retenait la
 * marchandise.
 *
 * Ce bouton lit la trace laissée par l'achat. S'il en trouve une pour le même
 * plan, il change de mot et rouvre la transaction existante au lieu d'en
 * ouvrir une seconde.
 */
export function BoutonAchat({
  plan,
  libelle,
  libelleReprise = "Récupérer mon rapport",
  className = "btn btn-brass",
  style,
}: {
  plan: PayPlan;
  libelle: string;
  libelleReprise?: string;
  className?: string;
  style?: CSSProperties;
}) {
  const { openPay, achat } = useApp();
  const repris = achat?.plan === plan;

  /*
    Un acheteur qui revient chercher son rapport n'est jamais une action
    secondaire.

    Le rang du bouton est un arbitrage de vente : on peut vouloir que
    « Débloquer les neuf autres » cède le premier rang au volet gratuit. Mais
    « Récupérer mon rapport » n'est plus une offre — c'est la livraison d'une
    chose payée. La reléguer au second plan reviendrait à retenir la
    marchandise, ce que ce composant existe précisément pour empêcher. La règle
    est donc écrite ici, une fois, et vaut pour chaque emplacement.
  */
  const classe = repris ? "btn btn-brass" : className;

  return (
    <>
      <button
        type="button"
        className={classe}
        style={style}
        onClick={() => openPay(plan)}
      >
        {repris ? libelleReprise : libelle}
      </button>
      {repris && (
        <p className="fine" style={{ marginTop: "8px" }}>
          Un paiement est déjà engagé depuis cet appareil. Ce bouton rouvre la même transaction —
          il n'en ouvre pas une seconde et ne vous redemande rien.
        </p>
      )}
    </>
  );
}
